/**
 * Default content for Media Gallery items.
 *
 * Shared by every path that creates an item — the gallery's starter template
 * and the "Add item" appender — so a freshly inserted gallery, a newly added
 * item, and the inserter preview all look the same.
 *
 * Keep DEMO_ITEMS in sync with the `example` block in MediaGallery/block.json;
 * block.json is static metadata and cannot import from here.
 */

const DEMO_ITEMS = [
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-1.webp',
    alt: 'Deck art laid out on a workbench',
    title: 'Deck art',
    description: 'Hand-screened graphics, one deck at a time.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-2.webp',
    alt: 'Rider mid-trick at the park',
    title: 'Park sessions',
    description: 'Shot on a Tuesday, before the crowds.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-3.webp',
    alt: 'Wheels and bearings on a shop counter',
    title: 'Parts and setups',
    description: 'Every build starts with the right wheels.',
  },
];

const TITLE_PLACEHOLDER = 'Item title (optional)';
const DESCRIPTION_PLACEHOLDER = 'Description (optional)';

/**
 * Inner-block layout for one item, pre-filled with demo content.
 *
 * Demo images are external URLs (no attachment id) — the same insert-by-link
 * shape the author gets from the image placeholder, so they render and open in
 * the lightbox as-is until swapped for real media.
 *
 * @param {number} index Item position; cycles through DEMO_ITEMS so repeated
 *                       inserts don't all land on the same image.
 * @return {Array} Inner-block template.
 */
export function getMediaGalleryItemTemplate(index = 0) {
  const demo = DEMO_ITEMS[index % DEMO_ITEMS.length];

  return [
    ['core/image', { url: demo.url, alt: demo.alt }],
    [
      'core/heading',
      { level: 3, content: demo.title, placeholder: TITLE_PLACEHOLDER },
    ],
    [
      'core/paragraph',
      { content: demo.description, placeholder: DESCRIPTION_PLACEHOLDER },
    ],
  ];
}

/**
 * Structure-only template — no demo content.
 *
 * Used as the item's own inner-blocks template, which only applies to items
 * that mount with zero inner blocks (galleries saved before items were seeded).
 * Those are existing author content, so it seeds empty fields rather than
 * injecting demo images into someone's saved gallery.
 *
 * Module-level constant on purpose: the block editor re-syncs inner blocks
 * whenever the template's identity changes, so this must not be rebuilt per render.
 */
export const MEDIA_GALLERY_ITEM_BLANK_TEMPLATE = [
  ['core/image', { sizeSlug: 'large' }],
  ['core/heading', { level: 3, placeholder: TITLE_PLACEHOLDER }],
  ['core/paragraph', { placeholder: DESCRIPTION_PLACEHOLDER }],
];

/**
 * The gallery's starter template: three demo-filled items.
 *
 * Module-level constant for the same identity-stability reason as above.
 */
export const MEDIA_GALLERY_STARTER_ITEMS = DEMO_ITEMS.map((_, index) => [
  'brandy/media-gallery-item',
  {},
  getMediaGalleryItemTemplate(index),
]);
